import { apiClient } from "./client";
import endpoint from "@/constants/endpoint";
import type {
  PreVettingDraft,
  PreVettingDraftResponse,
  UpsertPreVettingDraftRequest,
  DocumentUploadResponse,
  DocumentAccessData,
  PreVettingSubmitResponse,
} from "@chinese-vendor/schemas";

export const preVettingApi = {
  /** Load the saved draft — GET /api/v1/vendors/me/pre-vetting/draft */
  async getDraft(): Promise<PreVettingDraft> {
    const res = await apiClient.get<PreVettingDraftResponse>(endpoint.preVetting.draft);
    // Unwrap the payload envelope; consumers work with the flat draft shape.
    return res.data.payload;
  },

  /** Save draft progress — PUT /api/v1/vendors/me/pre-vetting/draft */
  async saveDraft(draft: PreVettingDraft): Promise<void> {
    const body: UpsertPreVettingDraftRequest = { payload: draft };
    await apiClient.put<null>(endpoint.preVetting.draft, body);
  },

  /**
   * Final submission — POST /api/v1/vendors/me/pre-vetting/submit
   * Throws APIError with status 422 if required fields are missing.
   * Field-level errors are not currently parsed from the response body.
   */
  async submit(): Promise<PreVettingSubmitResponse> {
    const res = await apiClient.post<PreVettingSubmitResponse>(endpoint.preVetting.submit, {});
    return res.data;
  },

  /**
   * Upload a document and return the public S3 URL.
   * POST /api/v1/vendors/me/documents/upload
   * Accepted: PDF, JPEG, PNG — max 10 MB.
   */
  async uploadDocument(
    file: { uri: string; name: string; type: string },
    onProgress?: (progress: number) => void
  ): Promise<string> {
    const res = await apiClient.upload<DocumentUploadResponse>(
      endpoint.preVetting.upload,
      file,
      onProgress
    );
    // Return the S3 key so it can be stored in draft and later resolved to a signed URL.
    return res.data.key;
  },

  /**
   * Get a time-limited presigned access URL for a previously uploaded document.
   * POST /api/v1/vendors/me/documents/access
   */
  async getDocumentAccessUrl(key: string): Promise<DocumentAccessData> {
    const res = await apiClient.post<DocumentAccessData>(endpoint.preVetting.documentAccess, {
      key,
      mode: "view",
    });
    return res.data;
  },
};
